import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import InventariosService from "../api/inventarios.api";
import { useBorradorLocal } from "./useBorradorLocal";
import { conteosDeLinea, fusionarConteos, quitarDelBorrador, totalContado } from "../helpers/conteos";

/** Valida que la cantidad sea un entero >= 0, sin depender de Number.isInteger sobre NaN. */
const esEnteroNoNegativo = (valor) => Number.isInteger(valor) && valor >= 0;

const MENSAJE_CANTIDAD_INVALIDA = "Introduce un número entero igual o mayor que 0.";
const MENSAJE_SIN_ALMACEN = "Elige primero el almacén donde estás contando.";

const SIN_BORRADOR = {};

const quitarClave = (objeto, clave) => {
    if (!(clave in objeto)) return objeto;
    const { [clave]: _omitida, ...resto } = objeto;
    return resto;
};

/**
 * Hook de conteo de un inventario: carga el detalle por id y lleva un BORRADOR
 * local de cantidades POR LÍNEA Y NAVE (puntear suma 1 en la nave elegida, o se
 * escribe la cantidad), que no viaja al servidor hasta `guardar()` o `finalizar()`.
 * Un artículo se puede contar en una, varias o todas las naves; cada una guarda
 * su propio conteo (`lineas[].conteos`, ver ../api/docs/api/inventarios.md).
 *
 * DECISION: borrador en cliente y guardado explícito, en vez de una petición
 * por cada punteo: escanear seguido dispararía decenas de PATCH por minuto.
 * El backend solo tiene PATCH por línea y nave, así que `guardar` los envía uno
 * tras otro (no en paralelo, para que no compitan en la transición a EN_CURSO)
 * y deja en el borrador, con su error, solo los que fallen.
 *
 * Nave activa de una línea: la elegida en esa tarjeta; si no, la general de la
 * pantalla ("estoy contando en..."), y si tampoco, la primera ya contada. Cambiar
 * la general descarta las elecciones por tarjeta: el preparador se ha movido de
 * nave. Sin nave activa no se puede contar.
 *
 * `quitarNave` deshace una nave contada por error: si solo estaba en el borrador
 * la descarta; si ya estaba guardada la borra en el servidor al momento.
 *
 * `inventarioInicial` evita un segundo GET cuando quien llama YA tiene el
 * inventario cargado (DetalleInventario): si su `id` coincide se usa como semilla.
 *
 * @param {string|number} id - Id del inventario (de la URL)
 * @param {Object|null} [inventarioInicial] - Inventario ya cargado por quien invoca el hook
 * @param {{persistirBorrador?: boolean}} [opciones] - persistirBorrador: copia el borrador en el
 *   dispositivo y lo recupera al volver (solo la pantalla de conteo; ver useBorradorLocal)
 * @returns {{
 *   inventario: Object|null,
 *   lineas: Array,
 *   loading: boolean,
 *   error: string|null,
 *   borrador: Object<number, Object<string, number>>,
 *   pendientes: number,
 *   lineasPendientes: Set<number>,
 *   guardando: boolean,
 *   erroresLinea: Object<number, string>,
 *   almacenDe: Function,
 *   almacenGeneral: string|null,
 *   seleccionarAlmacenGeneral: Function,
 *   recuperados: number,
 *   descartarRecuperados: Function,
 *   ocultarAvisoRecuperados: Function,
 *   lineasSinContar: Array,
 *   quitarNave: Function,
 *   quitandoNave: string|null,
 *   sumarUno: Function,
 *   fijarCantidad: Function,
 *   seleccionarAlmacen: Function,
 *   guardar: Function,
 *   finalizar: Function,
 *   puedeFinalizar: boolean
 * }} Estado del detalle, borrador de conteo y operaciones. Cada línea de `lineas`
 *   trae `conteos` con lo guardado y el borrador fusionados (`pendiente: true` si
 *   tiene cambios sin enviar, `guardado: true` si esa nave existe en el servidor) y
 *   `cantidadContada` con su total
 */
export const useDetalleInventario = (id, inventarioInicial = null, { persistirBorrador = false } = {}) => {
    const yaTieneInventarioInicial = Boolean(inventarioInicial) && inventarioInicial.id === Number(id);

    const [inventario, setInventario] = useState(inventarioInicial);
    const [loading, setLoading] = useState(!yaTieneInventarioInicial);
    const [error, setError] = useState(null);
    // { lineaId: { almacen: cantidad } }
    const [borrador, setBorrador] = useState({});
    const [almacenesElegidos, setAlmacenesElegidos] = useState({});
    const [almacenGeneral, setAlmacenGeneral] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [quitandoNave, setQuitandoNave] = useState(null);
    const [erroresLinea, setErroresLinea] = useState({});

    useEffect(() => {
        if (yaTieneInventarioInicial) return undefined;

        let vivo = true;
        setLoading(true);
        setError(null);

        InventariosService.getById(id)
            .then((data) => {
                if (vivo) setInventario(data);
            })
            .catch((err) => {
                if (vivo) setError(err.message || "No se pudo cargar el inventario solicitado.");
            })
            .finally(() => {
                if (vivo) setLoading(false);
            });

        return () => {
            vivo = false;
        };
    }, [id, yaTieneInventarioInicial]);

    const conteosGuardados = useCallback(
        (lineaId) => conteosDeLinea(inventario?.lineas?.find((linea) => linea.id === lineaId)),
        [inventario]
    );

    const cantidadGuardada = useCallback(
        (lineaId, almacen) => conteosGuardados(lineaId).find((conteo) => conteo.almacen === almacen)?.cantidadContada ?? null,
        [conteosGuardados]
    );

    const almacenDe = useCallback(
        (lineaId) => almacenesElegidos[lineaId] ?? almacenGeneral ?? conteosGuardados(lineaId)[0]?.almacen ?? null,
        [almacenesElegidos, almacenGeneral, conteosGuardados]
    );

    // Por línea del servidor, el último { enBorrador, resultado }: una línea sin cambios
    // conserva el mismo objeto y su tarjeta (React.memo) no se repinta
    const cacheLineas = useRef(new WeakMap());

    // Líneas tal y como se ven en pantalla: el borrador de cada nave manda sobre lo guardado
    const lineas = useMemo(
        () =>
            (inventario?.lineas ?? []).map((linea) => {
                const enBorrador = borrador[linea.id] ?? SIN_BORRADOR;
                const previa = cacheLineas.current.get(linea);
                if (previa?.enBorrador === enBorrador) return previa.resultado;

                const conteos = fusionarConteos(conteosDeLinea(linea), enBorrador);
                const resultado = { ...linea, conteos, cantidadContada: totalContado(conteos) };
                cacheLineas.current.set(linea, { enBorrador, resultado });
                return resultado;
            }),
        [inventario, borrador]
    );

    const cambios = useMemo(
        () =>
            Object.entries(borrador).flatMap(([lineaId, porAlmacen]) =>
                Object.entries(porAlmacen).map(([almacen, cantidad]) => ({ lineaId: Number(lineaId), almacen, cantidad }))
            ),
        [borrador]
    );

    const lineasPendientes = useMemo(() => new Set(cambios.map((cambio) => cambio.lineaId)), [cambios]);

    const { recuperados, descartarRecuperados, ocultarAviso } = useBorradorLocal({
        activo: persistirBorrador,
        inventario,
        borrador,
        setBorrador,
        almacenGeneral,
        setAlmacenGeneral,
    });

    // Volver al valor guardado no deja un cambio pendiente
    const anotar = useCallback(
        (lineaId, almacen, cantidad) => {
            setErroresLinea((prev) => quitarClave(prev, lineaId));
            setBorrador((prev) =>
                cantidad === cantidadGuardada(lineaId, almacen)
                    ? quitarDelBorrador(prev, lineaId, almacen)
                    : { ...prev, [lineaId]: { ...(prev[lineaId] ?? {}), [almacen]: cantidad } }
            );
        },
        [cantidadGuardada]
    );

    /** Devuelve la nave activa de la línea, o marca el error si aún no hay ninguna. */
    const almacenParaContar = useCallback(
        (lineaId) => {
            const almacen = almacenDe(lineaId);
            if (!almacen) setErroresLinea((prev) => ({ ...prev, [lineaId]: MENSAJE_SIN_ALMACEN }));
            return almacen;
        },
        [almacenDe]
    );

    /**
     * Puntear un artículo: suma 1 en su nave activa (sin contar cuenta como 0).
     * @returns {{almacen: string, cantidad: number}|null} La nave y la cantidad nueva, o null si no hay nave activa
     */
    const sumarUno = useCallback(
        (lineaId) => {
            const almacen = almacenParaContar(lineaId);
            if (!almacen) return null;
            const cantidad = (borrador[lineaId]?.[almacen] ?? cantidadGuardada(lineaId, almacen) ?? 0) + 1;
            anotar(lineaId, almacen, cantidad);
            return { almacen, cantidad };
        },
        [almacenParaContar, borrador, cantidadGuardada, anotar]
    );

    /** Fija la cantidad escrita a mano en la nave activa; un valor no entero >= 0 se marca como error. */
    const fijarCantidad = useCallback(
        (lineaId, cantidad) => {
            if (!esEnteroNoNegativo(cantidad)) {
                setErroresLinea((prev) => ({ ...prev, [lineaId]: MENSAJE_CANTIDAD_INVALIDA }));
                return;
            }
            const almacen = almacenParaContar(lineaId);
            if (!almacen) return;
            anotar(lineaId, almacen, cantidad);
        },
        [almacenParaContar, anotar]
    );

    /** Nave donde está contando el preparador, para todas las líneas. */
    const seleccionarAlmacenGeneral = useCallback((almacen) => {
        setAlmacenGeneral(almacen);
        setAlmacenesElegidos({});
        setErroresLinea((prev) => Object.fromEntries(Object.entries(prev).filter(([, mensaje]) => mensaje !== MENSAJE_SIN_ALMACEN)));
    }, []);

    /** Cambia la nave activa de la línea: lo que se cuente después va a esa nave. */
    const seleccionarAlmacen = useCallback((lineaId, almacen) => {
        setAlmacenesElegidos((prev) => ({ ...prev, [lineaId]: almacen }));
        setErroresLinea((prev) => quitarClave(prev, lineaId));
    }, []);

    /**
     * Deshace el conteo de una nave: descarta el borrador y, si estaba guardado, lo borra en el servidor.
     * @returns {Promise<boolean>} true si se quitó
     */
    const quitarNave = useCallback(
        async (lineaId, almacen) => {
            setErroresLinea((prev) => quitarClave(prev, lineaId));
            const descartarBorrador = () => setBorrador((prev) => quitarDelBorrador(prev, lineaId, almacen));

            if (cantidadGuardada(lineaId, almacen) === null) {
                descartarBorrador();
                return true;
            }

            setQuitandoNave(`${lineaId}|${almacen}`);
            try {
                setInventario(await InventariosService.eliminarConteo(id, lineaId, almacen));
                descartarBorrador();
                return true;
            } catch (err) {
                setErroresLinea((prev) => ({ ...prev, [lineaId]: err.message || "No se pudo quitar el conteo de esa nave." }));
                return false;
            } finally {
                setQuitandoNave(null);
            }
        },
        [cantidadGuardada, id]
    );

    /**
     * Envía al servidor los conteos pendientes. Se puede seguir punteando mientras
     * guarda: al acabar solo se retira del borrador lo enviado que no ha cambiado
     * entretanto; lo punteado durante el guardado queda pendiente.
     * @returns {Promise<boolean>} true si se guardaron todos
     */
    const guardar = useCallback(async () => {
        if (cambios.length === 0) return true;

        setGuardando(true);
        const fallos = {};
        const enviados = [];
        let ultimo = null;
        for (const cambio of cambios) {
            try {
                ultimo = await InventariosService.registrarConteo(id, cambio.lineaId, cambio.cantidad, cambio.almacen);
                enviados.push(cambio);
            } catch (err) {
                fallos[cambio.lineaId] = err.message || "No se pudo guardar la cantidad contada.";
            }
        }

        // Cada respuesta trae la lista entera: la última correcta ya incluye todas las anteriores
        if (ultimo) setInventario(ultimo);
        setBorrador((prev) =>
            enviados.reduce(
                (acumulado, { lineaId, almacen, cantidad }) =>
                    acumulado[lineaId]?.[almacen] === cantidad ? quitarDelBorrador(acumulado, lineaId, almacen) : acumulado,
                prev
            )
        );
        const lineasEnviadas = new Set(cambios.map((cambio) => cambio.lineaId));
        setErroresLinea((prev) => ({
            ...Object.fromEntries(Object.entries(prev).filter(([lineaId]) => !lineasEnviadas.has(Number(lineaId)))),
            ...fallos,
        }));
        setGuardando(false);
        return Object.keys(fallos).length === 0;
    }, [cambios, id]);

    const lineasSinContar = useMemo(() => lineas.filter((linea) => linea.conteos.length === 0), [lineas]);
    const puedeFinalizar = lineas.length > 0 && lineasSinContar.length === 0;

    // Guarda primero lo pendiente: finalizar con cambios sin enviar los perdería
    const finalizar = useCallback(async () => {
        if (!puedeFinalizar) {
            throw new Error("No se puede finalizar: quedan líneas sin contar.");
        }
        const guardadoCompleto = await guardar();
        if (!guardadoCompleto) {
            throw new Error("No se pudieron guardar todas las cantidades. Revisa las líneas marcadas.");
        }
        const actualizado = await InventariosService.finalizar(id);
        setInventario(actualizado);
        return actualizado;
    }, [id, puedeFinalizar, guardar]);

    return {
        inventario,
        lineas,
        loading,
        error,
        borrador,
        pendientes: cambios.length,
        lineasPendientes,
        guardando,
        erroresLinea,
        almacenDe,
        almacenGeneral,
        seleccionarAlmacenGeneral,
        recuperados,
        descartarRecuperados,
        ocultarAvisoRecuperados: ocultarAviso,
        lineasSinContar,
        quitarNave,
        quitandoNave,
        sumarUno,
        fijarCantidad,
        seleccionarAlmacen,
        guardar,
        finalizar,
        puedeFinalizar,
    };
};
